<script lang="ts" setup>
import AmazonProduct from "@/components/AmazonProduct.vue";
import AppTextMuted from "@/components/AppTextMuted.vue";
import BasePagination from "@/components/BasePagination.vue";
import BaseSearchBar from "@/components/BaseSearchBar.vue";
import DokkanCategory from "@/components/DokkanCategory.vue";
import TheLayout from "@/components/TheLayout.vue";
import { PaginatedData } from "@/types";
import { Category } from "@/types/category";
import { router } from "@inertiajs/vue3";
import {
  breakpointsTailwind,
  useBreakpoints,
  watchDebounced,
} from "@vueuse/core";
import _ from "lodash";
import { reactive } from "vue";
import { route } from "ziggy-js";

const breakpoints = useBreakpoints(breakpointsTailwind);

interface Props {
  categories: PaginatedData<Category>;
}

defineProps<Props>();

const filters = reactive({
  search: route().params.search ?? "",
});

watchDebounced(
  () => filters,
  () => {
    router.visit(route(route().current()), {
      data: _.omitBy(
        {
          ...filters,
          page: 1,
        },
        (val) => _.isNil(val) || val === "" || val === "0",
      ),
      only: ["categories"],
      preserveState: true,
    });
  },
  { deep: true },
);
</script>

<template>
  <TheLayout
    :heading-href="route('categories.index')"
    description="View all character categories available in DRAGON BALL Z Dokkan Battle, including category leaders and their leader skill effects, and support characters."
    has-page-heading
    heading="Categories"
    title="Dokkan Battle Categories"
  >
    <template #navbar>
      <div class="flex grow justify-between gap-4 md:grow-0">
        <BaseSearchBar v-model="filters.search" class="w-full" />
      </div>
    </template>

    <template v-for="(category, i) in categories.data" :key="category.id">
      <DokkanCategory :category="category" />

      <AmazonProduct
        v-if="i === 0"
        :amount="
          breakpoints.smallerOrEqual('sm').value
            ? 1
            : breakpoints.greaterOrEqual('lg').value
              ? 5
              : 3
        "
        class="col-span-full"
      />
    </template>

    <BasePagination :meta="categories.meta" />

    <AppTextMuted class="italic">
      * Partial Leader Skill; characters require specific additional categories
      to be eligible for bonuses or reach maximum multipliers. See the
      character's page for more details.
    </AppTextMuted>
  </TheLayout>
</template>

<style scoped></style>
